/**
 * Interval arithmetic over the expression AST.
 *
 * This exists to make the PRD §4.5 promise honest. Corner-based worst-case
 * analysis evaluates the function at the box's vertices; for a monotonic
 * function that finds the true extremes, but for a non-monotonic one the true
 * extreme can sit in the interior and every corner will miss it. A tool that
 * reports such a result as the "absolute" limit is confidently wrong about the
 * one number a worst-case analysis exists to produce.
 *
 * Interval evaluation instead produces a guaranteed *outer* bound: the true
 * range is always contained in it. It can be pessimistic — the dependency
 * problem means `x - x` evaluates to a non-zero interval — so it is reported
 * alongside the achievable corner result rather than replacing it.
 */

import type { Node } from './expr/ast.ts';

export interface Interval {
  lo: number;
  hi: number;
}

const TWO_PI = 2 * Math.PI;

export const iv = (lo: number, hi: number): Interval => ({ lo, hi });

function mulIv(a: Interval, b: Interval): Interval {
  const p = [a.lo * b.lo, a.lo * b.hi, a.hi * b.lo, a.hi * b.hi];
  return iv(Math.min(...p), Math.max(...p));
}

function divIv(a: Interval, b: Interval): Interval {
  // A divisor spanning zero admits arbitrarily large magnitudes.
  if (b.lo <= 0 && b.hi >= 0) return iv(-Infinity, Infinity);
  const p = [a.lo / b.lo, a.lo / b.hi, a.hi / b.lo, a.hi / b.hi];
  return iv(Math.min(...p), Math.max(...p));
}

function sinIv(a: Interval): Interval {
  if (a.hi - a.lo >= TWO_PI) return iv(-1, 1);
  const k = Math.floor(a.lo / TWO_PI);
  const lo = a.lo - k * TWO_PI;
  const hi = a.hi - k * TWO_PI;

  let min = Math.min(Math.sin(lo), Math.sin(hi));
  let max = Math.max(Math.sin(lo), Math.sin(hi));

  // hi < lo + 2pi <= 4pi, so crests at m = 0 and 1 cover the whole window.
  for (let m = 0; m <= 1; m++) {
    if (Math.PI / 2 + TWO_PI * m >= lo && Math.PI / 2 + TWO_PI * m <= hi) max = 1;
    if ((3 * Math.PI) / 2 + TWO_PI * m >= lo && (3 * Math.PI) / 2 + TWO_PI * m <= hi) min = -1;
  }
  return iv(min, max);
}

function cosIv(a: Interval): Interval {
  return sinIv(iv(a.lo + Math.PI / 2, a.hi + Math.PI / 2));
}

function tanIv(a: Interval): Interval {
  if (a.hi - a.lo >= Math.PI) return iv(-Infinity, Infinity);
  // A pole at pi/2 + k*pi inside the interval unbounds it.
  const kLo = Math.ceil((a.lo - Math.PI / 2) / Math.PI);
  const kHi = Math.floor((a.hi - Math.PI / 2) / Math.PI);
  if (kLo <= kHi) return iv(-Infinity, Infinity);
  return iv(Math.tan(a.lo), Math.tan(a.hi));
}

/** Apply a monotonically increasing function to both endpoints. */
function monotoneUp(a: Interval, f: (x: number) => number): Interval {
  return iv(f(a.lo), f(a.hi));
}

function powIv(base: Interval, exp: Interval): Interval {
  const isConst = exp.lo === exp.hi;

  if (isConst) {
    const n = exp.lo;
    if (Number.isInteger(n)) {
      if (n === 0) return iv(1, 1);
      if (n > 0) {
        if (n % 2 === 0) {
          if (base.lo <= 0 && base.hi >= 0) {
            return iv(0, Math.max(Math.pow(base.lo, n), Math.pow(base.hi, n)));
          }
          const p = [Math.pow(base.lo, n), Math.pow(base.hi, n)];
          return iv(Math.min(...p), Math.max(...p));
        }
        return iv(Math.pow(base.lo, n), Math.pow(base.hi, n)); // odd power: increasing
      }
      // Negative integer exponent: 1 / base^|n|
      return divIv(iv(1, 1), powIv(base, iv(-n, -n)));
    }
    // Non-integer exponent needs a non-negative base.
    if (base.lo < 0) return iv(-Infinity, Infinity);
    return n > 0
      ? iv(Math.pow(base.lo, n), Math.pow(base.hi, n))
      : iv(Math.pow(base.hi, n), Math.pow(base.lo, n));
  }

  // Varying exponent: fall back to exp(v * ln(u)), which needs a positive base.
  if (base.lo <= 0) return iv(-Infinity, Infinity);
  return monotoneUp(mulIv(exp, monotoneUp(base, Math.log)), Math.exp);
}

function absIv(a: Interval): Interval {
  if (a.lo >= 0) return a;
  if (a.hi <= 0) return iv(-a.hi, -a.lo);
  return iv(0, Math.max(-a.lo, a.hi));
}

/** Smallest interval containing both. Used to over-approximate branches. */
function hull(a: Interval, b: Interval): Interval {
  return iv(Math.min(a.lo, b.lo), Math.max(a.hi, b.hi));
}

/**
 * Evaluate `node` over intervals. `box` supplies each variable's interval;
 * unknown names throw, matching the compiler's behaviour.
 */
export function evalInterval(node: Node, box: ReadonlyMap<string, Interval>): Interval {
  switch (node.kind) {
    case 'num':
      return iv(node.value, node.value);

    case 'ref': {
      const b = box.get(node.name);
      if (!b) throw new Error(`Unknown name '${node.name}' in interval evaluation`);
      return b;
    }

    case 'neg': {
      const a = evalInterval(node.operand, box);
      return iv(-a.hi, -a.lo);
    }

    case 'binary': {
      const l = evalInterval(node.left, box);
      const r = evalInterval(node.right, box);
      switch (node.op) {
        case '+':
          return iv(l.lo + r.lo, l.hi + r.hi);
        case '-':
          return iv(l.lo - r.hi, l.hi - r.lo);
        case '*':
          return mulIv(l, r);
        case '/':
          return divIv(l, r);
        case '^':
          return powIv(l, r);
      }
      break;
    }

    case 'compare':
      // Without relational tracking a comparison over intervals may be either.
      return iv(0, 1);

    case 'if':
      // Conservative: the result is somewhere in the union of both branches.
      return hull(evalInterval(node.then, box), evalInterval(node.otherwise, box));

    case 'call': {
      const a = evalInterval(node.args[0], box);
      switch (node.name) {
        case 'sqrt':
          return iv(Math.sqrt(Math.max(0, a.lo)), Math.sqrt(Math.max(0, a.hi)));
        case 'exp':
          return monotoneUp(a, Math.exp);
        case 'ln':
          return iv(a.lo <= 0 ? -Infinity : Math.log(a.lo), a.hi <= 0 ? -Infinity : Math.log(a.hi));
        case 'log10':
          return iv(
            a.lo <= 0 ? -Infinity : Math.log10(a.lo),
            a.hi <= 0 ? -Infinity : Math.log10(a.hi),
          );
        case 'abs':
          return absIv(a);
        case 'sin':
          return sinIv(a);
        case 'cos':
          return cosIv(a);
        case 'tan':
          return tanIv(a);
        case 'asin':
          return iv(Math.asin(Math.max(-1, a.lo)), Math.asin(Math.min(1, a.hi)));
        case 'acos':
          return iv(Math.acos(Math.min(1, a.hi)), Math.acos(Math.max(-1, a.lo)));
        case 'atan':
          return monotoneUp(a, Math.atan);
        case 'atan2':
          // Exact bounds require quadrant analysis; the full range is always safe.
          return iv(-Math.PI, Math.PI);
        case 'floor':
          return monotoneUp(a, Math.floor);
        case 'ceil':
          return monotoneUp(a, Math.ceil);
        case 'round':
          return monotoneUp(a, Math.round);
        case 'min': {
          const b = evalInterval(node.args[1], box);
          return iv(Math.min(a.lo, b.lo), Math.min(a.hi, b.hi));
        }
        case 'max': {
          const b = evalInterval(node.args[1], box);
          return iv(Math.max(a.lo, b.lo), Math.max(a.hi, b.hi));
        }
        case 'clamp': {
          const lo = evalInterval(node.args[1], box);
          const hi = evalInterval(node.args[2], box);
          const lower = iv(Math.max(a.lo, lo.lo), Math.max(a.hi, lo.hi));
          return iv(Math.min(lower.lo, hi.lo), Math.min(lower.hi, hi.hi));
        }
      }
      break;
    }
  }

  throw new Error(`Unsupported node in interval evaluation`);
}
